import { useState, useRef } from "react";
import { ChevronDown, X, Check, Calendar as CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { tr, nl } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useLang } from "@/context/LanguageContext";

const baseInput =
  "w-full h-14 px-4 rounded-xl border border-line bg-paper text-ink text-base placeholder:text-stone/60 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand transition-shadow";

export function Field({ label, error, children, hint }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-1.5">{label}</span>
      {children}
      {hint && !error && <span className="block mt-1 text-xs text-stone">{hint}</span>}
      {error && <span className="block mt-1 text-xs text-red-600" data-testid="field-error">{error}</span>}
    </label>
  );
}

export function TextInput({ value, onChange, error, ...props }) {
  return (
    <input
      className={`${baseInput} ${error ? "border-red-500" : ""}`}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...props}
    />
  );
}

export function TextArea({ value, onChange, ...props }) {
  return (
    <textarea
      className={`${baseInput} h-28 py-3 resize-none`}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...props}
    />
  );
}

export function DateField({ value, onChange, placeholder = "GG/AA/YYYY", testId, futureOnly = false, error }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const locale = lang === "nl" ? nl : tr;
  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`${baseInput} flex items-center justify-between text-left ${error ? "border-red-500" : ""} ${!value ? "text-stone/60" : ""}`}
        >
          <span>{selected ? format(selected, "dd/MM/yyyy") : placeholder}</span>
          <CalendarIcon size={18} className="text-stone shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 border-line z-[80]" align="start" collisionPadding={16}>
        <Calendar
          mode="single"
          locale={locale}
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => {
            onChange(d ? format(d, "yyyy-MM-dd") : "");
            setOpen(false);
          }}
          disabled={futureOnly ? { before: new Date(new Date().setHours(0, 0, 0, 0)) } : undefined}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export function SelectField({ value, onChange, options, placeholder, error, testId }) {
  return (
    <div className="relative">
      <select
        data-testid={testId}
        className={`${baseInput} appearance-none pr-10 ${error ? "border-red-500" : ""} ${!value ? "text-stone/60" : ""}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled>{placeholder || "—"}</option>
        {options.map((o) => (
          <option key={o} value={o} className="text-ink">{o}</option>
        ))}
      </select>
      <ChevronDown size={18} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-stone" />
    </div>
  );
}

export function Pills({ value, onChange, options, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            type="button"
            key={o}
            onClick={() => onChange(o)}
            className={`h-12 px-4 rounded-xl border text-sm font-medium transition-colors ${
              active ? "bg-brand text-white border-brand" : "bg-paper text-ink border-line hover:border-brand"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

export function Checkbox({ checked, onChange, children, error, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        data-testid={testId}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 shrink-0 h-6 w-6 rounded-md border flex items-center justify-center transition-colors ${
          checked ? "bg-brand border-brand" : `bg-paper ${error ? "border-red-500" : "border-line"}`
        }`}
      >
        {checked && <Check size={15} strokeWidth={3} className="text-white" />}
      </button>
      <span className="text-sm text-stone leading-relaxed">{children}</span>
    </label>
  );
}

const NL_CITIES = [
  "Amsterdam", "Rotterdam", "Den Haag", "Utrecht", "Eindhoven", "Groningen",
  "Tilburg", "Almere", "Breda", "Nijmegen", "Apeldoorn", "Haarlem", "Arnhem",
  "Enschede", "Amersfoort", "Zaanstad", "Zaandam", "'s-Hertogenbosch", "Den Bosch",
  "Haarlemmermeer", "Hoofddorp", "Zwolle", "Zoetermeer", "Leiden", "Leeuwarden",
  "Maastricht", "Dordrecht", "Ede", "Alphen aan den Rijn", "Westland", "Alkmaar",
  "Emmen", "Delft", "Venlo", "Deventer", "Sittard-Geleen", "Sittard", "Geleen",
  "Helmond", "Oss", "Amstelveen", "Hilversum", "Heerlen", "Súdwest-Fryslân",
  "Purmerend", "Roosendaal", "Schiedam", "Spijkenisse", "Nissewaard", "Lelystad",
  "Vlaardingen", "Almelo", "Gouda", "Hoorn", "Bergen op Zoom", "Assen", "Capelle aan den IJssel",
  "Veenendaal", "Katwijk", "Zeist", "Nieuwegein", "Hardenberg", "Roermond",
  "Doetinchem", "Hengelo", "Terneuzen", "Barneveld", "Middelburg", "Vlissingen",
  "Kampen", "Woerden", "Rijswijk", "Waalwijk", "Ridderkerk", "Weert", "Wageningen",
  "Zutphen", "Houten", "IJsselstein", "Tiel", "Kerkrade", "Uden", "Veghel",
  "Heerhugowaard", "Etten-Leur", "Nieuwkoop", "Culemborg", "Harderwijk", "Zevenaar",
  "Rheden", "Meppel", "Steenwijk", "Coevorden", "Winterswijk", "Oldenzaal",
  "Goes", "Sneek", "Drachten", "Heerenveen", "Emmeloord", "Franeker", "Dokkum",
  "Zaltbommel", "Gorinchem", "Sliedrecht", "Papendrecht", "Hardinxveld-Giessendam",
  "Bodegraven", "Boskoop", "Waddinxveen", "Leiderdorp", "Leidschendam", "Voorburg",
  "Wassenaar", "Voorschoten", "Noordwijk", "Lisse", "Hillegom", "Sassenheim",
  "Beverwijk", "Heemskerk", "Velsen", "IJmuiden", "Castricum", "Uithoorn",
  "Aalsmeer", "Diemen", "Weesp", "Hoogeveen", "Stadskanaal", "Veendam", "Delfzijl",
  "Winschoten", "Beilen", "Bussum", "Naarden", "Huizen", "Blaricum", "Laren",
  "Soest", "Baarn", "Nijkerk", "Putten", "Ermelo", "Nunspeet", "Elburg",
  "Doorn", "Driebergen", "Bunnik", "Maarssen", "Bilthoven", "Vianen", "Leerdam",
  "Cuijk", "Boxmeer", "Gennep", "Grave", "Boxtel", "Vught", "Rosmalen",
  "Best", "Veldhoven", "Geldrop", "Nuenen", "Valkenswaard", "Bladel", "Someren",
  "Deurne", "Gemert", "Beek", "Stein", "Brunssum", "Landgraaf", "Heythuysen",
  "Venray", "Panningen", "Echt", "Maasbracht", "Valkenburg", "Meerssen",
];

export function CityTags({ value = [], onChange, placeholder, error, testId }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const q = text.trim().toLowerCase();
  const pool = NL_CITIES.filter((c) => !value.includes(c));
  const suggestions = (q
    ? pool
        .filter((c) => c.toLowerCase().includes(q))
        .sort((a, b) => {
          const ap = a.toLowerCase().startsWith(q) ? 0 : 1;
          const bp = b.toLowerCase().startsWith(q) ? 0 : 1;
          return ap - bp || a.localeCompare(b);
        })
    : pool
  ).slice(0, 8);

  const add = (city) => {
    const c = city.trim();
    if (c && !value.includes(c)) onChange([...value, c]);
    setText("");
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <div className={`min-h-14 rounded-xl border ${error ? "border-red-500" : "border-line"} bg-paper p-2 flex flex-wrap gap-2 focus-within:ring-2 focus-within:ring-brand`}>
        {value.map((c) => (
          <span key={c} className="inline-flex items-center gap-1.5 h-9 pl-3 pr-2 rounded-lg bg-brand text-white text-sm">
            {c}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== c))} aria-label={`remove ${c}`}><X size={14} /></button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[120px] h-9 px-1 bg-transparent text-base focus:outline-none placeholder:text-stone/60"
          value={text}
          placeholder={value.length === 0 ? placeholder : ""}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(text); } }}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-paper border border-line rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto">
          {suggestions.map((s) => (
            <button key={s} type="button" onMouseDown={() => add(s)} className="w-full text-left px-4 py-2.5 text-sm hover:bg-mist transition-colors">{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}
